'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { clearStoreIdCache } from '@/hooks/useStoreId';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function getSubUserFromStorage() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('ef_subuser_session');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);

      if (session?.user) {
        // Real Supabase auth user — clear any stale sub-user session
        if (typeof window !== 'undefined') {
          localStorage.removeItem('ef_subuser_session');
        }
        setUser(session.user);
      } else {
        // No Supabase session — check for sub-user session in localStorage
        const subUser = getSubUserFromStorage();
        if (subUser) {
          setUser({ id: subUser.id, email: subUser.username, isSubUser: true, subUserData: subUser });
        } else {
          setUser(null);
        }
      }

      setLoading(false);
    }).catch(() => {
      // Network error — fall back to sub-user session or unauthenticated state
      const subUser = getSubUserFromStorage();
      if (subUser) {
        setUser({ id: subUser.id, email: subUser.username, isSubUser: true, subUserData: subUser });
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);

      if (session?.user) {
        // Real Supabase auth user logged in — clear sub-user session
        if (typeof window !== 'undefined') {
          localStorage.removeItem('ef_subuser_session');
        }
        setUser(session.user);
      } else {
        // Supabase signed out or no session — check if there's a sub-user session
        // Use a small delay to allow localStorage to be written by the login handler first
        setTimeout(() => {
          const subUser = getSubUserFromStorage();
          if (subUser) {
            setUser({ id: subUser.id, email: subUser.username, isSubUser: true, subUserData: subUser });
          } else {
            setUser(null);
          }
        }, 100);
      }

      setLoading(false);
    });

    // Listen for sub-user login events (dispatched by LoginScreen after writing localStorage)
    const handleSubUserLogin = (e: Event) => {
      const subUser = (e as CustomEvent).detail;
      if (subUser) {
        setUser({ id: subUser.id, email: subUser.username, isSubUser: true, subUserData: subUser });
      }
      setLoading(false);
    };

    // Listen for sub-user logout events
    const handleSubUserLogout = () => {
      setUser(null);
      setLoading(false);
    };

    window.addEventListener('ef_subuser_login', handleSubUserLogin);
    window.addEventListener('ef_subuser_logout', handleSubUserLogout);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('ef_subuser_login', handleSubUserLogin);
      window.removeEventListener('ef_subuser_logout', handleSubUserLogout);
    };
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    // Immediately update state so the UI reflects the new session without waiting for the event
    if (data.session?.user) {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('ef_subuser_session');
      }
      setSession(data.session);
      setUser(data.session.user);
    }
    return data;
  };

  // Sign Out
  const signOut = async () => {
    // Clear sub-user session if present
    if (typeof window !== 'undefined') {
      localStorage.removeItem('ef_subuser_session');
      window.dispatchEvent(new CustomEvent('ef_subuser_logout'));
    }
    clearStoreIdCache();
    setUser(null);
    setSession(null);
    // Only call Supabase signOut if there's an actual Supabase session
    if (session) {
      const { error } = await supabase.auth.signOut();
      if (error) console.warn('signOut error:', error.message);
    }
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
